// apps/web/src/lib/integration-brands.ts
//
// LAT-1165 — client-side Connected Source integration identity. The source
// catalog is still code-pinned server-side, so the web map can stay closed and
// additive until the catalog becomes dynamic enough to stamp an icon field.

import type { BrandKey } from "@/lib/brand-keys";

export type IntegrationIdentity = {
  brand: BrandKey;
  label: string;
};

export type IntegrationBrandRow = {
  /** Boundary adapter name (e.g. "nango") — the wire provider, never copy. */
  provider: string;
  /** Provider-side integration key (e.g. "linear", "github"). */
  integration: string;
  brand: BrandKey;
  /** Vendor-free customer-facing name used when the caller has no label. */
  label: string;
};

// The client-side brand mirror for CONNECTED SOURCES. Adding a source means
// adding ONE row here — no per-integration branching. The source catalog is
// still code-pinned server-side and does NOT stamp a brand key onto the
// web-facing catalog, so this registry is the mirror for sync.
//
// LAT-1996 — tools Connectors no longer appear here. Their catalog stamps a
// brand key on every row, so the tools picker reads the server rather than a
// table someone has to remember to extend. A server-stamped brand field on the
// SOURCE catalog is the follow-up that would retire this table too.
export const INTEGRATION_BRAND_REGISTRY: readonly IntegrationBrandRow[] = [
  { provider: "nango", integration: "linear", brand: "linear", label: "Linear" },
  {
    provider: "nango",
    integration: "google-drive",
    brand: "google-drive",
    label: "Google Drive",
  },
  { provider: "nango", integration: "github", brand: "github", label: "GitHub" },
];

function titleize(slug: string): string {
  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function integrationIdentityForSource({
  provider,
  integration,
  label,
}: {
  provider: string;
  integration: string;
  label?: string | null;
}): IntegrationIdentity {
  const row = INTEGRATION_BRAND_REGISTRY.find(
    (r) => r.provider === provider && r.integration === integration,
  );
  if (row) {
    return { brand: row.brand, label: label ?? row.label };
  }
  return { brand: "model", label: label ?? titleize(integration) };
}
